import { useEffect, useState } from "react"

import useSnackBarContext from "@contexts/snackBarContext"
import { useAuthContext } from "@contexts/authContext"
import { useSearchParams } from "react-router-dom"
import { MapView } from "@contexts/mapDataContext"
import { colors, size } from "@styles/theme"
import snakecaseKeys from "snakecase-keys"
import useLoading from "@hooks/useLoading"
import styled from "@emotion/styled"

import { Button, Accordion, AccordionSummary, AccordionDetails } from "@mui/material"
import { NearbyGeneralFormSection } from './NearbyGeneralFormSection'
import BackTopbar, { TopbarClearfix } from '@elements/BackTopbar'
import { IdentityFormSection } from './IdentityFormSection'
import { LinearProgress } from '@elements/LinearProgress'
import { SocialFormSection } from './SocialFormSection'
import { HealthFormSection } from './HealthFormSection'
import { StatsFormSection } from './StatsFormSection'
import { SceneFormSection } from './SceneFormSection'
import ModalContainer from '@elements/ModalContainer'
import { NearbyImageForm } from './NearbyImageForm'
import { SocialImageForm } from './SocialImageForm'
import { PiCaretDownBold } from "react-icons/pi"
import { Toast } from '@base-ui/react'

const Contents = styled.div`
  height: 100%;
  width: 100%;
  overflow-y: scroll;
  display: flex;
  flex-direction: column;
`

const ContentsMain = styled.div`
  flex-grow: 1;
  width: 100%;
  display: flex;
  flex-direction: column;
`

const Form = styled.form``

const StyledAccordian = styled(Accordion)`
  background-color: transparent;
  box-shadow: none;
  margin: 0 !important;

  &::before {
    display: none;
  }
`

const AccSummary = styled(AccordionSummary)`
  background-color: rgba(255,255,255,0.075);
  font-weight: bold;
`

const AccDetails = styled(AccordionDetails)`
  display: flex;
  flex-direction: column;
  gap: ${size(4)};
  
  padding: ${size(2)} ${size(1)};
  border-radius: 0;

  background-color: transparent;
`

const Topbar = ({ titleText, isLoading, handleSubmit }) => (
  <BackTopbar title={titleText || 'Profile Information'}>
    <Button
      disabled={isLoading}
      variant='contained'
      onClick={handleSubmit}
      style={{ marginRight: size(1), borderRadius: '100px', backgroundColor: colors.statusColors.right_now('primary') }}>
      Save
    </Button>
  </BackTopbar>
)

const CaretDown = () => (
  <PiCaretDownBold fill='white' size={size(2)} />
)

const toString = (numberVal) => {
  if (numberVal) {
    return `${numberVal}`
  } else {
    return ''
  }
}

const toSet = (arrVals) => {
  if (Array.isArray(arrVals)) {
    return new Set(arrVals)
  } else {
    return new Set()
  }
}

const enumLimits = {
  expression: 3,
  hostLocation: Infinity,
  sexuallyInto: 3,
  lookingFor: 3,
  kinks: 3,
  rolesSituations: 3,
  interactions: 3,
  fetishes: 3,
}

const defaultSocialSettings = (user) => ({
  displayName: user?.displayName || '',
  bio: user?.bio || '',
  homeLocation: user?.homeLocation || '',
  link: user?.link || '',
})

const defaultExtendedInfos = (extendedInfos) => ({
  tagline: extendedInfos?.tagline || '',

  // stats
  age: toString(extendedInfos?.age),
  height: toString(extendedInfos?.height),
  weight: toString(extendedInfos?.weight),
  endowmentSize: toString(extendedInfos?.endowmentSize),
  endowmentCut: extendedInfos?.endowmentCut ?? 'endowment_cut_unspecified',
  bodyType: extendedInfos?.bodyType ?? 'body_type_unspecified',

  // identity
  gender: extendedInfos?.gender || 'gender_unspecified',
  expression: toSet(extendedInfos?.expression),
  sexuality: extendedInfos?.sexuality || 'sexuality_unspecified',
  position: extendedInfos?.position || 'position_unspecified',

  // scene
  hostLocation: toSet(extendedInfos?.hostLocation),
  sexuallyInto: toSet(extendedInfos?.sexuallyInto),
  lookingFor: toSet(extendedInfos?.lookingFor),
  kinks: toSet(extendedInfos?.kinks),
  rolesSituations: toSet(extendedInfos?.rolesSituations),
  interactions: toSet(extendedInfos?.interactions),
  fetishes: toSet(extendedInfos?.fetishes),

  // health & safety
  practices: extendedInfos?.practices || 'practices_unspecified',
  hivStatus: extendedInfos?.hivStatus || 'hiv_status_unspecified',
})

const NearbySettingsDialog = ({ isOpen, setClosed, afterSubmit, setUser }) => {
  const { isLoading, isLoadingDelayed, setIsLoading } = useLoading()
  const { handleNearbyUpdate, state: authState, handleInstantUpdate } = useAuthContext()
  const [searchParams] = useSearchParams()
  const { addSnackBar } = useSnackBarContext()
  const extendedInfos = authState?.user?.extendedInfos
  const [socialSettingsState, setSocialSettingsState] = useState(defaultSocialSettings(authState?.user))
  const [extendedSettingsState, setExtendedSettingsState] = useState(defaultExtendedInfos(extendedInfos))
  const defaultDisplay = searchParams.get('form_display')
  const titleText = searchParams.get('title')
  const toastManager = Toast.useToastManager()

  // Sets state on initial render
  useEffect(() => {
    if (isOpen) {
      setExtendedSettingsState(defaultExtendedInfos(extendedInfos))
      setSocialSettingsState(defaultSocialSettings(authState?.user))
    }
  }, [isOpen])

  // Updates tagline when changed elsewhere
  useEffect(() => {
    const currentTagline = authState?.user?.extendedInfos?.tagline
    if (currentTagline && currentTagline !== extendedSettingsState?.tagline) {
      setExtendedSettingsState({ ...extendedSettingsState, tagline: currentTagline })
    }
  }, [authState?.user?.extendedInfos?.tagline])

  const handleExtendedChange = (e) => {
    setExtendedSettingsState({ ...extendedSettingsState, [e.target.name]: e.target.value });
  }

  const handleEnumChange = (property, e) => {
    const newExtendedSettingsState = { ...extendedSettingsState }
    if (e?.target?.checked) {
      if (newExtendedSettingsState[property]?.size < enumLimits[property]) {
        newExtendedSettingsState?.[property]?.add?.(e.target.name)
      } else {
        toastManager.add({
          text: `Can only select ${enumLimits[property]}`,
        })
      }
    } else {
      newExtendedSettingsState?.[property]?.delete?.(e.target.name)
    }
    setExtendedSettingsState(newExtendedSettingsState)
  }

  const handleSocialChange = (e) => {
    setSocialSettingsState({ ...socialSettingsState, [e.target.name]: e.target.value });
  }

  const generateExtendedReqData = () => {
    const extended_infos_attributes = { ...extendedSettingsState }
    Object.keys(extended_infos_attributes).forEach(key => {
      if (extended_infos_attributes[key] instanceof Set) {
        extended_infos_attributes[key] = [...extended_infos_attributes[key]]
      }
    })
    return extended_infos_attributes
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    setIsLoading(true)

    const data = snakecaseKeys({
      ...socialSettingsState,
      extended_infos_attributes: generateExtendedReqData(),
    })

    handleNearbyUpdate(data)
      .then((res) => {
        const updatedUser = res.data
        MapView.updateUser(updatedUser?.nearbyUuid, { ...updatedUser })
        if (typeof afterSubmit === 'function') afterSubmit(res.data);
        setClosed()
        setIsLoading(false)
        addSnackBar({ message: 'Profile updated' })
      })
      .catch(() => {
        setIsLoading(false)
        addSnackBar({ message: 'Profile failed to update', kind: 'error' })
      })
  }

  const renderCustomHeader = () => (
    <Topbar
      titleText={titleText}
      isLoading={isLoading}
      handleSubmit={handleSubmit}
    />
  )

  const handleExtendedClear = (accessor, newValue) => {
    setExtendedSettingsState({ ...extendedSettingsState, [accessor]: newValue })
  }

  return(
    <ModalContainer
      renderCustomHeader={renderCustomHeader}
      open={isOpen}
      setClosed={setClosed}>
      <TopbarClearfix style={{ height: size(7) }} />
      <LinearProgress isVisible={isLoadingDelayed} />
      <Contents>
        <ContentsMain>
          <Form onSubmit={handleSubmit}>
            <StyledAccordian defaultExpanded={defaultDisplay?.includes('nearby')}>
              <AccSummary expandIcon={<CaretDown />}>
                &nbsp;&nbsp;Nearby Profile
              </AccSummary>
              <AccDetails>
                <NearbyImageForm setUser={setUser} />
                <NearbyGeneralFormSection
                  state={extendedSettingsState}
                  handleExtendedChange={handleExtendedChange}
                />
                <StatsFormSection
                  state={extendedSettingsState}
                  handleExtendedChange={handleExtendedChange}
                  handleClear={handleExtendedClear}
                />
                <IdentityFormSection
                  state={extendedSettingsState}
                  handleExtendedChange={handleExtendedChange}
                  handleEnumChange={handleEnumChange}
                  handleClear={handleExtendedClear}
                />
                <SceneFormSection
                  state={extendedSettingsState}
                  handleExtendedChange={handleExtendedChange}
                  handleEnumChange={handleEnumChange}
                  handleClear={handleExtendedClear}
                />
                <HealthFormSection
                  state={extendedSettingsState}
                  handleExtendedChange={handleExtendedChange}
                  handleEnumChange={handleEnumChange}
                  handleClear={handleExtendedClear}
                />
              </AccDetails>
            </StyledAccordian>
            {!authState?.user?.isAnonymous &&
              <StyledAccordian defaultExpanded={defaultDisplay?.includes('social')}>
                <AccSummary expandIcon={<CaretDown />}>
                  &nbsp;&nbsp;Social Profile
                </AccSummary>
                <AccDetails>
                  <SocialImageForm setUser={setUser} />
                  <SocialFormSection
                    state={socialSettingsState}
                    handleSocialChange={handleSocialChange}
                  />
                </AccDetails>
              </StyledAccordian>
            }
          </Form>
        </ContentsMain>
      </Contents>
    </ModalContainer>
  )
}

export default NearbySettingsDialog
